/**
 * The side a member is working on right now: buying or selling.
 *
 * Every member trades both ways. The side the profile recorded leads, and
 * after that the member's own last choice does: whoever switched the
 * dashboard to «فروش» comes back to the selling side, and the nav row and the
 * phone tab for their listings open the selling list. The choice lives in this
 * browser only; nothing on the server depends on it.
 */

import { useCallback, useSyncExternalStore } from 'react'

export type TradeSide = 'buy' | 'sell'

const KEY = 'metal.trade-side'
const EVENT = 'metal:trade-side'

function readStored(): TradeSide | null {
  try {
    const v = window.localStorage.getItem(KEY)
    return v === 'buy' || v === 'sell' ? v : null
  } catch {
    return null
  }
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener('storage', onChange)
  window.addEventListener(EVENT, onChange)
  return () => {
    window.removeEventListener('storage', onChange)
    window.removeEventListener(EVENT, onChange)
  }
}

/**
 * The current side and a setter. `sides` is the account's own list, leading
 * side first; a stored choice the account cannot use is ignored.
 */
export function useTradeSide(sides: TradeSide[]): [TradeSide, (side: TradeSide) => void] {
  const stored = useSyncExternalStore(subscribe, readStored, () => null)
  const side: TradeSide = stored && sides.includes(stored) ? stored : (sides[0] ?? 'sell')
  const set = useCallback((next: TradeSide) => {
    try {
      window.localStorage.setItem(KEY, next)
    } catch {
      // A private window may refuse storage; the URL still carries the view.
    }
    window.dispatchEvent(new Event(EVENT))
  }, [])
  return [side, set]
}

/** «خرید | فروش», the one switch the dashboard and both listing pages share. */
export const TRADE_SIDE_OPTIONS: Array<{ value: TradeSide; label: string }> = [
  { value: 'buy', label: 'خرید' },
  { value: 'sell', label: 'فروش' },
]
